// ============================================================
// دستیار سامانه — پنجره‌ی گفتگو
//
// این کامپوننت داخل Layout نشسته و در همه‌ی صفحه‌های محافظت‌شده
// هست. چون Layout با هر تغییر مسیر دوباره mount می‌شود، گفتگو در
// sessionStorage نگه داشته می‌شود؛ وگرنه هر ناوبری، حافظه‌ی دستیار
// را پاک می‌کرد.
//
// نکته‌ی نگه‌داری: تابع‌های format ستون‌ها از JSON رد نمی‌شوند، پس
// بعد از یک ناوبری، جدول‌های قبلی مقدار خام را نشان می‌دهند. این
// عمدی است — جایگزینش نگه داشتن کل نتیجه در حافظه‌ی سراسری بود که
// پیچیدگی‌اش به این معامله نمی‌ارزید.
// ============================================================

import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { createSelector } from "@reduxjs/toolkit";
import {
    AudioLines,
    Bell,
    BellOff,
    Bot,
    Eraser,
    FileDown,
    Filter,
    Maximize2,
    Mic,
    MicOff,
    Minimize2,
    PhoneOff,
    Send,
    Sparkles,
    Volume2,
    VolumeX,
    X,
} from "lucide-react";

import RecordView from "./RecordView";
import { currentRole, roleLabel } from "./lib/access";
import { chime, loadChimePreference, setChimeEnabled } from "./lib/chime";
import { datasetById, visibleDatasets } from "./lib/datasets";
import { exportDataset } from "./lib/exporter";
import { isCloudTtsEnabled } from "./lib/llm";
import { flushNavToast, queueNavToast, showNavToast } from "./lib/navToast";
import {
    AssistantFilter,
    announcePageFilter,
    currentPageId,
    putPageFilter,
} from "./lib/pageFilter";
import {
    AssistantMessage,
    Pending,
    QuickAction,
    rephrase,
    respond,
    summarizeResult,
    Turn,
} from "./lib/engine";
import {
    hasPersianVoice,
    isSpeechSupported,
    loadSpeakPreference,
    lastSpeechFailure,
    setSpeakEnabled,
    speak,
    stopSpeaking,
    unlockCloudAudio,
} from "./lib/speak";
import {
    DownloadProgress,
    downloadFailure as piperDownloadFailure,
    ensureDownloaded as ensurePiperVoice,
    isDownloaded as isPiperDownloaded,
    isSupported as isPiperSupported,
} from "./lib/piperVoice";
import {
    ConversationLoop,
    ConversationState,
    isConversationSupported,
} from "./lib/conversation";
import { useVoiceInput } from "./lib/voice";
import { toPersianDigits } from "./lib/text";

const STORE_KEY = "havir-assistant-session";
const MAX_KEPT = 30;
/** مکث عمدی: کاربر باید پیام تأیید را ببیند و کار ناگهانی نباشد. */
const NAVIGATE_DELAY_MS = 900;

// حالت تمام‌صفحه یعنی «تمام ناحیه‌ی محتوا»، نه تمام viewport: هدر و
// منوی کناری باید دیده شوند. همان state ای که Sidebar و Header با آن
// اندازه می‌گیرند اینجا هم خوانده می‌شود، وگرنه با فشرده شدن منو یا
// برگشتن جهت صفحه، پنل روی منو می‌افتاد.
const selectLayoutBox = createSelector(
    (state: any) => state.Layout,
    (layout) => ({
        sidebarSize: layout.layoutSidebarSizeType,
        direction: layout.layoutDirectionType,
        navigation: layout.layoutNavigationType,
    })
);

interface StoredState {
    open: boolean;
    maximized: boolean;
    messages: AssistantMessage[];
    pending: Pending | null;
}

const loadState = (): StoredState => {
    try {
        const raw = sessionStorage.getItem(STORE_KEY);
        if (!raw) return { open: false, maximized: false, messages: [], pending: null };
        const parsed = JSON.parse(raw);
        return {
            open: !!parsed.open,
            maximized: !!parsed.maximized,
            messages: Array.isArray(parsed.messages) ? parsed.messages : [],
            pending: parsed.pending || null,
        };
    } catch {
        return { open: false, maximized: false, messages: [], pending: null };
    }
};

const saveState = (state: StoredState) => {
    try {
        sessionStorage.setItem(
            STORE_KEY,
            JSON.stringify({
                open: state.open,
                maximized: state.maximized,
                messages: state.messages.slice(-MAX_KEPT),
                pending: state.pending,
            })
        );
    } catch {
        /* حالت خصوصی مرورگر — دستیار باید بی‌حافظه هم کار کند */
    }
};

const AiAssistant: React.FC = () => {
    const navigate = useNavigate();
    const initial = useMemo(loadState, []);

    const [open, setOpen] = useState(initial.open);
    const [maximized, setMaximized] = useState(initial.maximized);
    const [messages, setMessages] = useState<AssistantMessage[]>(initial.messages);
    const [pending, setPending] = useState<Pending | null>(initial.pending);
    const [input, setInput] = useState("");
    const [busy, setBusy] = useState(false);
    const [voiceError, setVoiceError] = useState("");
    const [speakOn, setSpeakOn] = useState(false);
    const [persianVoice, setPersianVoice] = useState(false);
    // صدای ابری به صدای نصب‌شده‌ی دستگاه وابسته نیست
    const cloudVoice = useMemo(() => isCloudTtsEnabled(), []);
    const [voiceDownload, setVoiceDownload] = useState<DownloadProgress | null>(null);
    const [soundOn, setSoundOn] = useState(true);
    const [convoState, setConvoState] = useState<ConversationState>("off");

    const reportSpeechError = useCallback((reason: string) => {
        // اگر لایه‌ی ابری شکست خورده، مقصر اصلی همان است — نه نبودِ
        // صدای فارسی روی دستگاه.
        if (lastSpeechFailure() === "cloud") {
            setVoiceError(
                "سرور صدا جواب نداد. اگر روی محیط توسعه هستید، مطمئن شوید " +
                    "`npm run start:assistant-openai` در حال اجراست."
            );
            return;
        }

        setVoiceError(
            reason === "no-persian-voice"
                ? "صدای فارسی پیدا نشد. دانلود خودکار صدا را دوباره امتحان کنید."
                : "مرورگر نتوانست صدا را پخش کند. صدای دستگاه را بررسی کنید و دوباره امتحان کنید."
        );
    }, []);

    const prepareOfflineVoice = useCallback(async (): Promise<boolean> => {
        if (hasPersianVoice()) {
            setPersianVoice(true);
            return true;
        }
        if (!isPiperSupported()) {
            setVoiceError("این مرورگر امکان آماده‌کردن صدای فارسی را ندارد. لطفاً از نسخه‌ی جدید Edge یا Chrome استفاده کنید.");
            return false;
        }
        if (await isPiperDownloaded()) {
            setPersianVoice(true);
            setVoiceDownload(null);
            return true;
        }

        setVoiceError("");
        setVoiceDownload({ loaded: 0, total: 0, percent: -1, label: "در حال آماده‌کردن صدای فارسی…" });
        const ready = await ensurePiperVoice(setVoiceDownload);
        setVoiceDownload(null);
        if (!ready) {
            const reason = piperDownloadFailure();
            setVoiceError(
                "دانلود صدای فارسی کامل نشد" +
                    (reason ? " (" + reason + ")" : "") +
                    ". اتصال را بررسی کنید و دوباره صدا را روشن کنید؛ دانلود از همان‌جا ادامه پیدا می‌کند."
            );
            return false;
        }
        setPersianVoice(true);
        return true;
    }, []);

    const speakWhenReady = useCallback(async (text: string, onDone?: () => void) => {
        // صدای ابری نه به صدای نصب‌شده‌ی دستگاه وابسته است نه به مدل
        // محلی؛ پس وقتی هست، معطل آماده‌سازی صدای محلی نمی‌مانیم.
        // (اگر همان لحظه در دسترس نبود، speak خودش به لایه‌ی محلی
        // می‌افتد.)
        if (cloudVoice) {
            /*
              عمداً مدل محلی را از قبل و در پس‌زمینه آماده نمی‌کنیم:
              کپی ۶۳ مگابایتی پهنای باند را می‌گرفت و همان درخواست صدا
              را تایم‌اوت می‌کرد («سرور صدا جواب نداد» در حالی که سرور
              سالم بود).

              ولی اگر صدای ابری *واقعاً* نشد، همان‌جا و بی‌درنگ صدای
              محلی را آماده می‌کنیم و همین جمله را با آن می‌خوانیم — تا
              دستیار به خاطر یک خطای شبکه لال نماند. یک بار، نه حلقه.
            */
            let retried = false;

            speak(text, onDone, (reason) => {
                const cloudBroke = lastSpeechFailure() === "cloud";
                if (!cloudBroke || retried) {
                    reportSpeechError(reason);
                    return;
                }

                retried = true;
                void prepareOfflineVoice().then((ready) => {
                    if (ready) speak(text, onDone, reportSpeechError);
                    else reportSpeechError(reason);
                });
            });
            return;
        }

        if (!hasPersianVoice() && !(await prepareOfflineVoice())) {
            if (onDone) onDone();
            return;
        }
        speak(text, onDone, reportSpeechError);
    }, [cloudVoice, prepareOfflineVoice, reportSpeechError]);

    const scrollerRef = useRef<HTMLDivElement>(null);
    // نقش با هر باز شدن پنل دوباره از توکن خوانده می‌شود؛ ممکن است
    // کاربر در همین نشست خارج و با حساب دیگری وارد شده باشد.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    const role = useMemo(() => currentRole(), [open]);

    const { sidebarSize, direction, navigation } = useSelector(selectLayoutBox);
    const rtl = direction !== "ltr";

    // کلاس‌ها عمداً به‌صورت رشته‌ی کامل نوشته شده‌اند تا Tailwind هنگام
    // اسکن فایل ببیندشان؛ ساختنشان با الحاق رشته، کلاس را حذف می‌کند.
    const maximizedBox = useMemo(() => {
        // بالا: درست زیر هدر. وقتی نوار بالا پنهان است، هدری هم در کار
        // نیست (Header خودش با navbar=hidden مخفی می‌شود).
        const top =
            navigation === "hidden"
                ? "top-2"
                : "top-[calc(theme('spacing.header')_+_0.5rem)]";

        // کنار: اعداد از خود Sidebar می‌آیند، نه از توکن‌های قالب — آن
        // منو دست‌ساز است: زیر ۱۰۲۴px کشویی می‌شود (پس جا نمی‌گیرد)، و
        // فقط در حالت فشرده ۸۰px است وگرنه ۲۶۰px.
        // عرض حالت فشرده روی hover به ۲۶۰px می‌رسد؛ عمداً دنبالش
        // نمی‌کنیم تا پنل با رد شدن ماوس نلرزد.
        const compact = sidebarSize === "sm";
        let sides: string;
        if (rtl) {
            sides = compact
                ? "left-2 right-2 lg:right-[calc(80px_+_0.5rem)]"
                : "left-2 right-2 lg:right-[calc(260px_+_0.5rem)]";
        } else {
            sides = compact
                ? "right-2 left-2 lg:left-[calc(80px_+_0.5rem)]"
                : "right-2 left-2 lg:left-[calc(260px_+_0.5rem)]";
        }

        return "fixed z-[60] bottom-2 " + top + " " + sides;
    }, [navigation, rtl, sidebarSize]);

    const showMicDebug = useMemo(
        () => typeof window !== "undefined" && window.location.search.indexOf("micdebug=1") !== -1,
        []
    );

    // ---------- نگه داشتن گفتگو بین صفحه‌ها ----------
    useEffect(() => {
        saveState({ open, maximized, messages, pending });
    }, [open, maximized, messages, pending]);

    // ---------- ترجیح صدا و موجود بودن صدای فارسی ----------
    useEffect(() => {
        setSpeakOn(loadSpeakPreference());
        setSoundOn(loadChimePreference());

        const check = () => setPersianVoice(hasPersianVoice());
        if (!isSpeechSupported()) return;
        check();
        window.speechSynthesis.addEventListener("voiceschanged", check);
        // اولین getVoices() معمولاً خالی است؛ یک بار دیگر هم می‌پرسیم
        const timer = setTimeout(check, 1200);

        return () => {
            window.speechSynthesis.removeEventListener("voiceschanged", check);
            clearTimeout(timer);
        };
    }, []);

    useEffect(() => {
        const node = scrollerRef.current;
        if (node) node.scrollTop = node.scrollHeight;
    }, [messages, busy, open, maximized]);

    // در حالت تمام‌صفحه، Esc مثل ویندوز به اندازه‌ی عادی برمی‌گردد؛
    // پنل را نمی‌بندد تا گفتگو با یک کلید اشتباهی از جلوی چشم نرود.
    useEffect(() => {
        if (!open || !maximized) return;

        const onKeyDown = (event: KeyboardEvent) => {
            if (event.key === "Escape") setMaximized(false);
        };

        window.addEventListener("keydown", onKeyDown);
        return () => window.removeEventListener("keydown", onKeyDown);
    }, [open, maximized]);

    // پیام تأیید ناوبری که قبل از عوض شدن صفحه در نوبت گذاشته شده بود.
    // Layout با هر تغییر مسیر دوباره mount می‌شود، پس همین‌جا درست است.
    useEffect(() => {
        flushNavToast();
    }, []);

    const append = useCallback((items: AssistantMessage[]) => {
        if (!items.length) return;
        setMessages((prev) => prev.concat(items).slice(-MAX_KEPT));

        /*
          جمله‌ی جواب بعد از نمایش روان می‌شود، نه قبلش.

          قبلاً جواب تا آمدن مدل معطل می‌ماند و به هر پاسخ یک تا سه
          ثانیه اضافه می‌شد — صدا هم همان‌قدر دیر پخش می‌شد. حالا جدول
          و صدا فوری می‌آیند و این فقط همان یک خط را عوض می‌کند.

          اگر مدل جواب نداد، جمله‌ی محلی سر جایش می‌ماند؛ هیچ خطایی به
          کاربر نشان داده نمی‌شود چون چیزی خراب نشده.
        */
        items.forEach((message) => {
            if (!message.rephrase) return;

            void rephrase(message.rephrase.question, message.rephrase.summary, role).then(
                (better) => {
                    if (!better) return;
                    setMessages((prev) =>
                        prev.map((item) =>
                            item.id === message.id
                                ? { ...item, text: better, source: "llm", rephrase: undefined }
                                : item
                        )
                    );
                }
            );
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [role]);

    /** رفتن به یک صفحه با پیام تأیید دو ثانیه‌ای و (اختیاراً) فیلتر. */
    const goToPage = useCallback(
        (to: string, title: string, filter?: AssistantFilter) => {
            if (filter) putPageFilter(filter);

            const parts: string[] = [];
            if (filter?.search) parts.push("جست‌وجو: " + filter.search);
            if (filter?.status)
                parts.push("وضعیت: " + (filter.status === "done" ? "انجام‌شده" : "انجام‌نشده"));
            if (filter?.startDate)
                parts.push(
                    "تاریخ: " +
                        toPersianDigits(
                            filter.startDate === filter.endDate
                                ? filter.startDate
                                : filter.startDate + " تا " + filter.endDate
                        )
                );
            if (filter?.pageSize) parts.push(toPersianDigits(filter.pageSize) + " سطر");

            queueNavToast(
                "«" + title + "»",
                parts.length ? parts.join(" · ") : "صفحه باز شد"
            );

            setTimeout(() => navigate(to), NAVIGATE_DELAY_MS);
        },
        [navigate]
    );

    /** خروجی اکسل — همه‌ی رکوردها، نه فقط صفحه‌ی اول. */
    const runExport = useCallback(
        async (datasetId: string, values: Record<string, string>) => {
            const dataset = datasetById(datasetId);
            if (!dataset) return;

            setBusy(true);
            try {
                const output = await exportDataset(dataset, values);
                chime("done");
                const text =
                    output.count === 0
                        ? "رکوردی برای خروجی نبود، پس فایلی ساخته نشد."
                        : "خروجی اکسل «" + output.title + "» با " +
                        toPersianDigits(output.count) + " رکورد دانلود شد: " + output.fileName;

                append([
                    { id: "x" + Date.now(), role: "assistant", text, source: "command" },
                ]);

                if (output.count > 0) {
                    showNavToast("خروجی اکسل آماده شد", toPersianDigits(output.count) + " رکورد");
                }
                void speakWhenReady(text);
            } catch {
                const text = "ساختن خروجی اکسل ناموفق بود. چند لحظه بعد دوباره تلاش کنید.";
                append([
                    { id: "x" + Date.now(), role: "assistant", text, source: "command" },
                ]);
            } finally {
                setBusy(false);
            }
        },
        [append, speakWhenReady]
    );

    // ---------- ارسال پیام ----------
    const send = useCallback(
        async (raw: string) => {
            const text = String(raw || "").trim();
            if (!text || busy) {
                // گفت‌وگو منتظر جواب ماست و جوابی در کار نیست؛ اگر
                // همین‌جا رهایش کنیم میکروفون دیگر باز نمی‌شود.
                conversationRef.current?.resumeIfIdle();
                return;
            }

            stopSpeaking();
            setInput("");
            setVoiceError("");

            const userMessage: AssistantMessage = {
                id: "u" + Date.now(),
                role: "user",
                text,
            };
            setMessages((prev) => prev.concat([userMessage]).slice(-MAX_KEPT));
            setBusy(true);

            try {
                // ۱۲ پیام آخر تا گفت‌وگو پیوسته بماند و «همونو توضیح
                // بده» معنا داشته باشد.
                const history: Turn[] = messages
                    .slice(-12)
                    .map((m) => ({ role: m.role, content: m.text }));

                const lastResult = messages
                    .filter((m) => !!m.result)
                    .slice(-1)[0];

                const outcome = await respond({
                    message: text,
                    role,
                    pending,
                    history,
                    lastResultSummary: lastResult?.result
                        ? summarizeResult(lastResult.result)
                        : undefined,
                    // خودِ نتیجه هم می‌رود: «اطلاعاتشو بگو» باید همین
                    // ردیف‌ها را بخواند، نه اینکه دوباره از سرور بپرسد.
                    lastResult: lastResult?.result,
                    currentPage: currentPageId(),
                });

                setPending(outcome.pending);

                if (outcome.effect) {
                    switch (outcome.effect.type) {
                        case "clear":
                            setMessages([]);
                            setPending(null);
                            setBusy(false);
                            return;
                        case "close":
                            append(outcome.messages);
                            setTimeout(() => setOpen(false), 700);
                            break;
                        case "navigate": {
                            const { link, title, filter } = outcome.effect;
                            append(outcome.messages);
                            goToPage(link, title || "صفحه", filter);
                            break;
                        }

                        case "apply-filter": {
                            // کاربر همین حالا روی همان صفحه است؛ ناوبری لازم
                            // نیست، فقط صفحه باید فیلتر را بردارد.
                            const { filter, title } = outcome.effect;
                            announcePageFilter(filter);
                            append(outcome.messages);
                            // فیلتر خالی یعنی «برداشته شد»؛ گفتن «اعمال شد»
                            // درست بعد از دستور پاک کردن، گیج‌کننده است.
                            showNavToast(
                                filter.search || filter.status
                                    ? "فیلتر «" + title + "» اعمال شد"
                                    : "فیلتر «" + title + "» برداشته شد",
                                filter.search || undefined
                            );
                            break;
                        }

                        case "export": {
                            const { datasetId, values } = outcome.effect;
                            append(outcome.messages);
                            runExport(datasetId, values);
                            break;
                        }
                        case "speak-on":
                            setSpeakOn(true);
                            setSpeakEnabled(true);
                            append(outcome.messages);
                            break;
                        case "speak-off":
                            setSpeakOn(false);
                            setSpeakEnabled(false);
                            append(outcome.messages);
                            break;
                        case "voice-on":
                            append(outcome.messages);
                            voiceRef.current.start();
                            break;
                        case "voice-off":
                            append(outcome.messages);
                            voiceRef.current.stop();
                            break;
                        default:
                            append(outcome.messages);
                    }
                } else {
                    append(outcome.messages);
                }

                // صدای اعلان: اگر سوالی پرسیده شده لحن «منتظرم»، وگرنه
                // لحن «انجام شد». کاربر از روی صدا می‌فهمد باید جواب
                // بدهد، بدون اینکه به صفحه نگاه کند.
                if (outcome.messages.length) {
                    chime(outcome.pending ? "ask" : "done");
                }

                // در گفتگوی پیوسته، میکروفون تا تمام شدن جمله بسته
                // می‌ماند و درست بعدش باز می‌شود — وگرنه دستیار حرف
                // خودش را می‌شنود.
                if (outcome.spoken) {
                    const loop = conversationRef.current;
                    if (loop && loop.isActive()) {
                        loop.pause("speaking");
                        void speakWhenReady(outcome.spoken, () => loop.resume());
                    } else {
                        void speakWhenReady(outcome.spoken);
                    }
                }
            } catch {
                append([
                    {
                        id: "e" + Date.now(),
                        role: "assistant",
                        text: "یک جای کار ایراد داشت. دوباره بپرسید.",
                        source: "system",
                    },
                ]);
            } finally {
                // هر مسیری که به خواندن نرسید — پاک کردن گفتگو، خطا،
                // پاسخ بی‌صدا — باید میکروفون را برگرداند. وقتی دستیار
                // دارد حرف می‌زند این تابع کاری نمی‌کند و resume واقعی
                // را همان onDone خواندن انجام می‌دهد.
                conversationRef.current?.resumeIfIdle();
                setBusy(false);
            }
        },
        [append, busy, goToPage, messages, pending, role, runExport, speakWhenReady]
    );

    // ---------- میکروفون ----------
    // در طراحی تک‌دستوری، وقتی جواب می‌رسد میکروفون از قبل بسته شده،
    // پس صدای دستیار وارد میکروفون نمی‌شود. اگر روزی حالت پیوسته
    // برگشت، باید قبل از speak شرط بگذارید که میکروفون باز نباشد.
    const voice = useVoiceInput({
        onResult: (text) => {
            setInput("");
            send(text);
        },
        onInterim: (text) => setInput(text),
        onError: (message) => setVoiceError(message),
    });

    // send و voice به هم ارجاع می‌دهند؛ ref این حلقه را باز می‌کند.
    const voiceRef = useRef(voice);
    voiceRef.current = voice;

    // ---------- گفت‌وگوی پیوسته ----------
    //
    // حلقه یک بار ساخته می‌شود و تا unmount زنده می‌ماند. هندلرهایش
    // هر رندر تازه‌اند ولی خودش ساخته نمی‌شود، وگرنه با هر پیام
    // میکروفون قطع و وصل می‌شد.
    const conversationRef = useRef<ConversationLoop | null>(null);
    const sendRef = useRef(send);
    sendRef.current = send;

    if (!conversationRef.current && isConversationSupported()) {
        conversationRef.current = new ConversationLoop({
            onUtterance: (text) => sendRef.current(text),
        });
    }

    conversationRef.current?.setHandlers({
        onUtterance: (text) => sendRef.current(text),
        onPartial: (text) => setInput(text),
        onState: (state) => {
            setConvoState(state);
            if (state !== "listening") setInput("");
        },
        onError: (message) => {
            setVoiceError(message);
            setConvoState("off");
        },
    });

    useEffect(() => {
        const loop = conversationRef.current;
        return () => loop?.stop();
    }, []);

    /**
     * روشن/خاموش کردن گفت‌وگو.
     *
     * روشن کردنش صدا را هم روشن می‌کند: گفت‌وگوی یک‌طرفه که دستیار
     * فقط بشنود و جوابش را بنویسد، معنا ندارد. و اولین speak باید
     * داخل همین کلیک باشد وگرنه سافاری بی‌صدا ردش می‌کند.
     */
    const toggleConversation = useCallback(() => {
        const loop = conversationRef.current;
        if (!loop) return;

        if (loop.isActive()) {
            loop.stop();
            stopSpeaking();
            setConvoState("off");
            return;
        }

        setVoiceError("");
        if (!speakOn) {
            setSpeakOn(true);
            setSpeakEnabled(true);
        }
        loop.startPaused("speaking");
        void speakWhenReady("گفت‌وگو روشن شد. بپرسید.", () => loop.resume());

    }, [speakOn, speakWhenReady]);

    // ---------- خوش‌آمد، یک بار در هر نشست ----------
    useEffect(() => {
        if (!open || messages.length) return;

        const datasets = visibleDatasets(role);
        const actions: QuickAction[] = datasets
            .slice(0, 5)
            .map((d) => ({ label: d.title, send: d.title }));

        setMessages([
            {
                id: "w" + Date.now(),
                role: "assistant",
                source: "system",
                text:
                    "سلام. دستیار سامانه‌ی رصد هستم.\n" +
                    "نقش شما: " + roleLabel(role) + " — " +
                    (datasets.length
                        ? "به " + toPersianDigits(datasets.length) + " گروه داده دسترسی دارید."
                        : "دسترسی داده‌ای برای نقش شما ثبت نشده است.") +
                    "\n\nمی‌توانید بپرسید «اطلاعات خبازی رضا محمدی» یا «گزارش دیروز»، " +
                    "بگویید «فلانی را فیلتر کن»، یا «خروجی اکسل صفحه اطلاعات خبازی».",
                actions: actions.length ? actions : undefined,
            },
        ]);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open]);

    const toggleSpeak = () => {
        const next = !speakOn;
        setSpeakOn(next);
        setSpeakEnabled(next);
        // قفل پخش سافاری را همین‌جا، داخل رویداد کلیک، باز می‌کنیم —
        // صدای ابری با تأخیر شبکه می‌آید و آن لحظه دیگر «داخل کلیک»
        // نیست.
        if (next) unlockCloudAudio();

        if (!next) {
            stopSpeaking();
            return;
        }

        setVoiceError("");
        // سافاری بدون تعامل کاربر حرف نمی‌زند: اولین speak باید مستقیماً
        // داخل رویداد کلیک باشد، وگرنه بی‌صدا رد می‌شود و بعد هم کار نمی‌کند.
        void speakWhenReady("حتماً، از این به بعد جواب‌ها رو با صدا می‌خونم.");

    };

    const closePanel = () => {
        stopSpeaking();
        if (voice.listening) voice.stop();
        // پنل بسته، میکروفون هم بسته. یک دستیار نامرئی که همچنان
        // گوش می‌دهد، چیزی است که هیچ کاربری انتظارش را ندارد.
        conversationRef.current?.stop();
        setConvoState("off");
        setOpen(false);
    };

    // ---------- دکمه‌ی شناور ----------
    if (!open) {
        return (
            <button
                type="button"
                onClick={() => setOpen(true)}
                aria-label="باز کردن دستیار سامانه"
                className="fixed z-[60] bottom-4 left-4 inline-flex items-center gap-2 px-4 h-14 rounded-full shadow-lg bg-custom-500 text-white transition-transform hover:scale-105 focus:outline-none"
            >
                <Bot className="size-6" />
                <span className="hidden text-sm font-medium sm:inline">دستیار هوشمند</span>
            </button>
        );
    }

    return (
        <div
            dir="rtl"
            className={
                // سایه‌ی عمیق + حلقه‌ی رنگی: از یک نگاه معلوم باشد که
                // پنجره‌ی دستیار باز است و روی محتوای صفحه نشسته.
                "flex flex-col bg-white dark:bg-zink-700 " +
                "ring-1 ring-custom-500/30 border border-slate-200 dark:border-zink-500 " +
                "shadow-[0_24px_70px_-20px_rgba(15,23,42,0.55)] " +
                (maximized
                    ? maximizedBox + " rounded-xl"
                    : "fixed z-[60] bottom-4 left-4 right-4 sm:right-auto sm:w-[26rem] max-h-[80vh] rounded-2xl")
            }
        >
            {/* ---------- سربرگ ---------- */}
            <div className="flex items-center gap-2 px-3 py-2.5 border-b border-slate-200 dark:border-zink-500">
                <span className="inline-flex items-center justify-center rounded-lg size-8 bg-custom-500/10 text-custom-500">
                    <Sparkles className="size-4" />
                </span>
                <div className="flex-1 min-w-0">
                    <div className="text-sm font-semibold text-slate-700 dark:text-zink-50">
                        دستیار سامانه
                    </div>
                    <div className="text-[10px] text-slate-400 dark:text-zink-300">
                        {roleLabel(role)}
                        {convoState === "listening"
                            ? " · گفت‌وگو: می‌شنوم…"
                            : convoState === "thinking"
                            ? " · گفت‌وگو: دارم نگاه می‌کنم…"
                            : convoState === "speaking"
                            ? " · گفت‌وگو: در حال پاسخ…"
                            : voice.listening
                            ? " · در حال شنیدن…"
                            : ""}
                    </div>
                </div>

                {/* با صدای ابری، نبودِ صدای فارسی در مرورگر اهمیتی ندارد؛
                    پس دکمه همیشه فعال است. در نبود صدای ابری، اولویت با
                    صدای فارسی مرورگر است و بعد نسخه‌ی داخلی. */}
                {(isSpeechSupported() || cloudVoice) && (
                    <button
                        type="button"
                        onClick={toggleSpeak}
                        aria-label={speakOn ? "خاموش کردن پاسخ صوتی" : "روشن کردن پاسخ صوتی"}
                        title={
                            cloudVoice
                                ? speakOn
                                    ? "خاموش کردن پاسخ صوتی (صدای اوپن‌ای‌آی)"
                                    : "روشن کردن پاسخ صوتی (صدای اوپن‌ای‌آی)"
                                : persianVoice
                                ? speakOn
                                    ? "خاموش کردن پاسخ صوتی"
                                    : "روشن کردن پاسخ صوتی"
                                : "صدای فارسی در مرورگر نیست؛ با روشن‌کردن صدا، نسخه‌ی داخلی آماده می‌شود"
                        }
                        className={
                            "inline-flex items-center justify-center rounded-lg size-8 transition-colors " +
                            (speakOn
                                ? persianVoice || cloudVoice
                                    ? "bg-custom-500/10 text-custom-500"
                                    : "bg-amber-500/10 text-amber-600"
                                : "text-slate-400 hover:bg-slate-100 dark:hover:bg-zink-600")
                        }
                    >
                        {speakOn ? <Volume2 className="size-4" /> : <VolumeX className="size-4" />}
                    </button>
                )}

                {/* صدای اعلان — پیش‌فرض روشن */}
                <button
                    type="button"
                    onClick={() => {
                        const next = !soundOn;
                        setSoundOn(next);
                        setChimeEnabled(next);
                        if (next) chime("done");
                    }}
                    aria-label={soundOn ? "خاموش کردن صدای اعلان" : "روشن کردن صدای اعلان"}
                    title={soundOn ? "خاموش کردن صدای اعلان" : "روشن کردن صدای اعلان"}
                    className={
                        "inline-flex items-center justify-center rounded-lg size-8 transition-colors " +
                        (soundOn
                            ? "bg-custom-500/10 text-custom-500"
                            : "text-slate-400 hover:bg-slate-100 dark:hover:bg-zink-600")
                    }
                >
                    {soundOn ? <Bell className="size-4" /> : <BellOff className="size-4" />}
                </button>

                <button
                    type="button"
                    onClick={() => {
                        setMessages([]);
                        setPending(null);
                        stopSpeaking();
                    }}
                    aria-label="پاک کردن گفتگو"
                    title="پاک کردن گفتگو"
                    className="inline-flex items-center justify-center rounded-lg size-8 text-slate-400 hover:bg-slate-100 dark:hover:bg-zink-600"
                >
                    <Eraser className="size-4" />
                </button>

                <button
                    type="button"
                    onClick={() => setMaximized((prev) => !prev)}
                    aria-label={maximized ? "خروج از حالت تمام‌صفحه" : "نمایش تمام‌صفحه"}
                    title={maximized ? "بازگشت به اندازه‌ی عادی" : "تمام‌صفحه"}
                    className="inline-flex items-center justify-center rounded-lg size-8 text-slate-400 hover:bg-slate-100 dark:hover:bg-zink-600"
                >
                    {maximized ? <Minimize2 className="size-4" /> : <Maximize2 className="size-4" />}
                </button>

                <button
                    type="button"
                    onClick={closePanel}
                    aria-label="بستن دستیار"
                    className="inline-flex items-center justify-center rounded-lg size-8 text-slate-400 hover:bg-slate-100 dark:hover:bg-zink-600"
                >
                    <X className="size-4" />
                </button>
            </div>

            {/* ---------- گفتگو ---------- */}
            <div ref={scrollerRef} className="flex-1 px-3 py-3 space-y-3 overflow-y-auto">
                {messages.map((message) => (
                    <div
                        key={message.id}
                        className={message.role === "user" ? "flex justify-start" : "flex justify-end"}
                    >
                        <div
                            className={
                                // در تمام‌صفحه، حباب‌ها با عرض ۹۲٪ به خطوط
                                // بسیار بلند و سخت‌خوان تبدیل می‌شدند.
                                (maximized ? "max-w-[46rem] " : "max-w-[92%] ") +
                                "rounded-xl px-3 py-2 text-xs leading-6 " +
                                (message.role === "user"
                                    ? "bg-custom-500 text-white"
                                    : "bg-slate-100 text-slate-700 dark:bg-zink-600 dark:text-zink-50")
                            }
                        >
                            <div className="whitespace-pre-wrap break-words">{message.text}</div>

                            {message.result && <RecordView result={message.result} />}

                            {(message.link || message.openWith || message.exportWith) && (
                                <div className="flex flex-wrap gap-1.5 mt-2">
                                    {message.openWith && (
                                        <button
                                            type="button"
                                            onClick={() =>
                                                goToPage(
                                                    message.openWith!.to,
                                                    message.result?.title || "صفحه",
                                                    message.openWith!.filter
                                                )
                                            }
                                            className="inline-flex items-center gap-1 px-2 py-1 text-[11px] font-medium rounded-md bg-custom-500 text-white hover:bg-custom-600"
                                        >
                                            <Filter className="size-3" />
                                            {message.openWith.label}
                                        </button>
                                    )}

                                    {message.exportWith && (
                                        <button
                                            type="button"
                                            disabled={busy}
                                            onClick={() =>
                                                runExport(
                                                    message.exportWith!.datasetId,
                                                    message.exportWith!.values
                                                )
                                            }
                                            className="inline-flex items-center gap-1 px-2 py-1 text-[11px] font-medium rounded-md bg-white/70 text-emerald-700 hover:bg-white disabled:opacity-40 dark:bg-zink-700 dark:text-emerald-300"
                                        >
                                            <FileDown className="size-3" />
                                            {message.exportWith.label}
                                        </button>
                                    )}

                                    {message.link && (
                                        <button
                                            type="button"
                                            onClick={() =>
                                                goToPage(message.link!.to, message.link!.label)
                                            }
                                            className="inline-flex items-center px-2 py-1 text-[11px] font-medium rounded-md bg-white/70 text-custom-600 hover:bg-white dark:bg-zink-700 dark:text-custom-300"
                                        >
                                            {message.link.label}
                                        </button>
                                    )}
                                </div>
                            )}

                            {message.actions && (
                                <div className="flex flex-wrap gap-1.5 mt-2">
                                    {message.actions.map((action) => (
                                        <button
                                            key={action.label}
                                            type="button"
                                            onClick={() => send(action.send)}
                                            className="px-2 py-1 text-[11px] rounded-md border border-slate-300 text-slate-600 hover:bg-white dark:border-zink-400 dark:text-zink-100 dark:hover:bg-zink-500"
                                        >
                                            {action.label}
                                        </button>
                                    ))}
                                </div>
                            )}
                        </div>
                    </div>
                ))}

                {busy && (
                    <div className="flex justify-end">
                        <div className="px-3 py-2 text-xs rounded-xl bg-slate-100 text-slate-400 dark:bg-zink-600 dark:text-zink-200">
                            در حال گرفتن داده…
                        </div>
                    </div>
                )}
            </div>

            {voiceDownload !== null && (
                <div className="px-3 py-2 text-[11px] text-slate-500 border-t border-slate-100 dark:border-zink-600">
                    <div className="flex justify-between gap-2 mb-1">
                        <span>در حال آماده‌کردن صدای فارسی…</span>
                        <span>{voiceDownload.label}</span>
                    </div>
                    <div className="h-1.5 overflow-hidden rounded-full bg-slate-200 dark:bg-zink-500">
                        <div
                            className="h-full rounded-full bg-custom-500 transition-[width] duration-300"
                            style={{ width: voiceDownload.percent < 0 ? "35%" : voiceDownload.percent + "%" }}
                        />
                    </div>
                </div>
            )}

            {/* ---------- خطای میکروفون ---------- */}
            {voiceError && (
                <div className="px-3 py-1.5 text-[11px] text-red-500 border-t border-slate-100 dark:border-zink-600">
                    {voiceError}
                </div>
            )}

            {/* ---------- ورودی ---------- */}
            <div className="flex items-end gap-2 px-3 py-2.5 border-t border-slate-200 dark:border-zink-500">
                {/* گفت‌وگوی پیوسته — یک بار روشن، تا خاموشش نکنی ادامه دارد */}
                {isConversationSupported() && (
                    <button
                        type="button"
                        onClick={toggleConversation}
                        aria-label={convoState === "off" ? "شروع گفت‌وگوی صوتی" : "پایان گفت‌وگو"}
                        title={
                            convoState === "off"
                                ? "گفت‌وگوی پیوسته — می‌پرسید، جواب می‌دهد، باز گوش می‌دهد"
                                : "پایان گفت‌وگو"
                        }
                        className={
                            "inline-flex items-center justify-center rounded-lg size-9 shrink-0 transition-colors " +
                            (convoState === "off"
                                ? "bg-slate-100 text-slate-500 hover:bg-slate-200 dark:bg-zink-600 dark:text-zink-100"
                                : convoState === "listening"
                                ? "bg-emerald-500 text-white animate-pulse"
                                : "bg-custom-500 text-white")
                        }
                    >
                        {convoState === "off" ? (
                            <AudioLines className="size-4" />
                        ) : (
                            <PhoneOff className="size-4" />
                        )}
                    </button>
                )}

                {voice.supported && convoState === "off" && (
                    <button
                        type="button"
                        onClick={() => {
                            stopSpeaking();
                            voice.toggle();
                        }}
                        aria-label={voice.listening ? "توقف شنیدن" : "حرف زدن"}
                        title={voice.listening ? "توقف شنیدن" : "حرف زدن"}
                        className={
                            "inline-flex items-center justify-center rounded-lg size-9 shrink-0 transition-colors " +
                            (voice.listening
                                ? "bg-red-500 text-white animate-pulse"
                                : "bg-slate-100 text-slate-500 hover:bg-slate-200 dark:bg-zink-600 dark:text-zink-100")
                        }
                    >
                        {voice.listening ? <MicOff className="size-4" /> : <Mic className="size-4" />}
                    </button>
                )}

                <textarea
                    value={input}
                    onChange={(event) => setInput(event.target.value)}
                    onKeyDown={(event) => {
                        if (event.key === "Enter" && !event.shiftKey) {
                            event.preventDefault();
                            send(input);
                        }
                    }}
                    rows={maximized ? 3 : 1}
                    placeholder={
                        pending ? "پاسخ سوال بالا را بنویسید…" : "هر سوالی دارید بپرسید که انجام بدم"
                    }
                    className="flex-1 px-3 py-2 text-xs border rounded-lg resize-none border-slate-200 bg-white text-slate-700 placeholder:text-slate-400 focus:outline-none focus:border-custom-500 dark:border-zink-500 dark:bg-zink-600 dark:text-zink-50"
                />

                <button
                    type="button"
                    onClick={() => send(input)}
                    disabled={busy || !input.trim()}
                    aria-label="ارسال"
                    className="inline-flex items-center justify-center rounded-lg size-9 shrink-0 bg-custom-500 text-white disabled:opacity-40"
                >
                    <Send className="size-4" />
                </button>
            </div>

            {showMicDebug && (
                <div className="px-3 pb-2 text-[10px] font-mono text-slate-400" dir="ltr">
                    {voice.debug || "idle"}
                </div>
            )}
        </div>
    );
};

export default AiAssistant;
